import { deterministicDraw } from './deterministic'
import type {
  AbilityGrade,
  GameState,
  InjuryBodyArea,
  InjuryEvent,
  InjurySeverity,
  Player,
  PlayerInjury,
  TeamPlayer,
  TeamSelection,
} from './types'

const SENIOR_GROUPS: (keyof TeamSelection)[] = [
  'backs', 'halfBacks', 'centre', 'halfForwards', 'forwards', 'followers', 'interchange',
]

const GRADE_RISK: Record<AbilityGrade, number> = {
  Limited: 1.55,
  'Below Standard': 1.3,
  'League Standard': 1,
  'Above Standard': 0.84,
  Strong: 0.7,
  Elite: 0.57,
  Exceptional: 0.46,
}

const INJURY_TYPES: Record<InjuryBodyArea, string[]> = {
  head: ['concussion'],
  shoulder: ['shoulder sprain', 'AC joint injury'],
  arm: ['wrist sprain', 'forearm fracture'],
  torso: ['rib injury', 'back spasm'],
  groin: ['adductor strain'],
  hamstring: ['hamstring strain'],
  knee: ['knee sprain', 'ACL injury'],
  ankle: ['ankle sprain', 'syndesmosis injury'],
  foot: ['foot stress injury'],
}
const BODY_AREAS = Object.keys(INJURY_TYPES) as InjuryBodyArea[]
const SEVERITIES: InjurySeverity[] = ['minor', 'moderate', 'major', 'season-ending']
const MAX_INJURY_WEEKS = 260

const careerIdentity = (state: GameState) => state.season?.careerSeed ?? `career-${state.season?.seed ?? state.seed}`
const fixtureIdentity = (state: GameState) => state.week?.fixtureId ?? `s${state.season?.season ?? 1}-r${state.round}`
const draw = (state: GameState, playerId: string, purpose: string) =>
  deterministicDraw(`${careerIdentity(state)}:${fixtureIdentity(state)}:${playerId}:injury:${purpose}`)

const tempoRisk = (state: GameState): number => {
  const tempo = state.tactics.find(tactic => tactic.id === 'D6.5')
  const label = tempo?.options.find(option => option.letter === tempo.currentChoice)?.label.toLowerCase() ?? 'balanced'
  if (label.includes('relentless') || label.includes('maximum')) return 1.3
  if (label.includes('controlled') || label.includes('slow')) return 0.78
  return 1
}

export const recurrenceCandidate = (player: Player, state: GameState): PlayerInjury | undefined => {
  const history = player.injuryHistory ?? []
  if (history.length === 0) return undefined

  // Select chronologically latest qualifying injury by occurredSeason desc,
  // occurredRound desc, stable ID tie-break.
  // This avoids trusting history array order.
  const currentSeason = state.season?.season ?? 1
  const currentRound = state.round
  const qualifying = history.filter(injury => {
    const seasonGap = currentSeason - injury.occurredSeason
    return seasonGap === 0 || (seasonGap === 1 && currentRound <= injury.occurredRound)
  })
  if (qualifying.length === 0) return undefined

  qualifying.sort((a, b) => {
    if (b.occurredSeason !== a.occurredSeason) return b.occurredSeason - a.occurredSeason
    if (b.occurredRound !== a.occurredRound) return b.occurredRound - a.occurredRound
    return a.id.localeCompare(b.id)
  })

  const recent = qualifying[0]
  // A previous injury increases risk slightly; the separately keyed draw makes
  // an actual same-area recurrence uncommon rather than inevitable.
  return draw(state, player.id, 'recurrence') < 0.09 ? recent : undefined
}

export const injuryRiskForPlayer = (state: GameState, player: Player, exposure = 1): number => {
  const ageFactor = player.age <= 21 ? 1.06 : player.age >= 32 ? 1.42 : player.age >= 29 ? 1.2 : 1
  const fitnessFactor = 1 + Math.max(0, 92 - player.fitness) / 75
  const fatigueFactor = 1 + Math.max(0, player.fatigue - 12) / 62
  const historyFactor = (player.injuryHistory?.length ?? 0) > 0 ? 1.08 : 1
  return Math.min(0.22, 0.021 * exposure * GRADE_RISK[player.abilities.durability] * ageFactor * fitnessFactor * fatigueFactor * tempoRisk(state) * historyFactor)
}

const severityFor = (roll: number): InjurySeverity => {
  if (roll < 0.7) return 'minor'
  if (roll < 0.94) return 'moderate'
  if (roll < 0.992) return 'major'
  return 'season-ending'
}

const weeksFor = (severity: InjurySeverity, roll: number, state: GameState): number => {
  if (severity === 'minor') return 1
  if (severity === 'moderate') return 2 + Math.floor(roll * 3)
  if (severity === 'major') return 5 + Math.floor(roll * 4)
  const finalRound = state.season ? Math.max(...state.season.fixtures.map(fixture => fixture.round)) : state.round + 10
  return Math.max(10, finalRound - state.round + 1)
}

export const generateMatchInjuries = (state: GameState): InjuryEvent[] => {
  const slotByPlayer = new Map<string, keyof TeamSelection>()
  SENIOR_GROUPS.forEach(group => state.staffPlan.team[group].forEach(entry => slotByPlayer.set(entry.playerId, group)))

  // Gather all eligible players with their independent injury priority draw
  // This ensures determinism regardless of roster order.
  const candidates: Array<{ player: Player; group: keyof TeamSelection; priority: number }> = []
  for (const player of state.club.players) {
    const group = slotByPlayer.get(player.id)
    if (!group || player.injury) continue
    const exposure = group === 'interchange' ? 0.78 : 1
    if (draw(state, player.id, 'occurrence') >= injuryRiskForPlayer(state, player, exposure)) continue
    // Independent keyed draw for stable ranking across roster reorders
    const priority = draw(state, player.id, 'injury-priority')
    candidates.push({ player, group, priority })
  }

  // Stable sort by priority draw, then stable player ID tie-break
  candidates.sort((a, b) => a.priority - b.priority || a.player.id.localeCompare(b.player.id))

  const events: InjuryEvent[] = []
  for (const { player } of candidates) {
    const prior = recurrenceCandidate(player, state)
    const areaRoll = draw(state, player.id, 'body-area')
    const bodyArea = prior?.bodyArea ?? BODY_AREAS[Math.floor(areaRoll * BODY_AREAS.length)]
    const types = INJURY_TYPES[bodyArea]
    const type = prior?.type ?? types[Math.floor(draw(state, player.id, 'type') * types.length)]
    const severity = severityFor(draw(state, player.id, 'severity'))
    const initialWeeks = weeksFor(severity, draw(state, player.id, 'duration'), state)
    const season = state.season?.season ?? 1
    const recurrenceCount = prior ? prior.recurrenceCount + 1 : 0
    const id = `injury-s${season}-r${state.round}-${player.id}-${bodyArea}`
    events.push({
      id,
      playerId: player.id,
      player: player.name,
      severity,
      bodyArea,
      type,
      description: `${player.name} sustained ${type.startsWith('A') ? 'an' : 'a'} ${type}${prior ? ' (recurrence)' : ''}.`,
      estimatedWeeks: initialWeeks,
      initialWeeks,
      occurredSeason: season,
      occurredRound: state.round,
      isRecurrence: Boolean(prior),
      recurrenceCount,
      previousInjuryId: prior?.id,
    })
  }

  // The 24-player trial list has only two players outside the senior 22.
  // Preserve enough healthy depth to field a complete team next week.
  // Only count existing injuries with remainingWeeks > 1 as consuming next-week depth,
  // since one-week injuries will clear during the same advancement.
  const activeInjuriesNextWeek = state.club.players.filter(
    player => player.injury && player.injury.remainingWeeks > 1
  ).length
  const healthyDepth = state.club.players.length - 22 - activeInjuriesNextWeek
  return events.slice(0, Math.max(0, healthyDepth))
}

const eventToInjury = (event: InjuryEvent): PlayerInjury => ({
  id: event.id,
  playerId: event.playerId,
  player: event.player,
  severity: event.severity,
  bodyArea: event.bodyArea,
  type: event.type,
  initialWeeks: event.initialWeeks,
  remainingWeeks: event.initialWeeks,
  occurredSeason: event.occurredSeason,
  occurredRound: event.occurredRound,
  isRecurrence: event.isRecurrence,
  recurrenceCount: event.recurrenceCount,
  previousInjuryId: event.previousInjuryId,
})

/** Apply one completed week. Existing injuries recover once; match injuries start at full duration. */
export const applyWeeklyInjuries = (players: Player[], newEvents: InjuryEvent[]): Player[] => {
  const incoming = new Map(newEvents.map(event => [event.playerId, event]))
  return players.map(player => {
    const event = incoming.get(player.id)
    if (event) {
      const injury = eventToInjury(event)
      const history = [...(player.injuryHistory ?? [])]
      if (!history.some(entry => entry.id === injury.id)) history.push(injury)
      return { ...player, injury, injuryHistory: history, isSelected: false, positionInTeam: null, roleInTeam: null }
    }
    if (!player.injury) return { ...player, injury: null, injuryHistory: player.injuryHistory ?? [] }
    const remainingWeeks = player.injury.remainingWeeks - 1
    return {
      ...player,
      injury: remainingWeeks > 0 ? { ...player.injury, remainingWeeks } : null,
      injuryHistory: player.injuryHistory ?? [player.injury],
      isSelected: remainingWeeks > 0 ? false : player.isSelected,
      positionInTeam: remainingWeeks > 0 ? null : player.positionInTeam,
      roleInTeam: remainingWeeks > 0 ? null : player.roleInTeam,
    }
  })
}

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const boundedInteger = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
const boundedString = (value: unknown, max = 120): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max

const hydrateInjury = (value: unknown, player: Player, current: boolean): PlayerInjury | null => {
  if (!isRecord(value)
    || !boundedString(value.id)
    || !SEVERITIES.includes(value.severity as InjurySeverity)
    || !BODY_AREAS.includes(value.bodyArea as InjuryBodyArea)
    || !boundedString(value.type)
    || !boundedInteger(value.initialWeeks, 1, MAX_INJURY_WEEKS)
    || !boundedInteger(value.remainingWeeks, current ? 1 : 0, MAX_INJURY_WEEKS)
    || value.remainingWeeks > value.initialWeeks
    || !boundedInteger(value.occurredSeason, 1, 1000)
    || !boundedInteger(value.occurredRound, 1, 100)
    || typeof value.isRecurrence !== 'boolean'
    || !boundedInteger(value.recurrenceCount, 0, 100)
    || (value.previousInjuryId !== undefined && !boundedString(value.previousInjuryId))) return null
  return {
    id: value.id,
    playerId: player.id,
    player: player.name,
    severity: value.severity as InjurySeverity,
    bodyArea: value.bodyArea as InjuryBodyArea,
    type: value.type,
    initialWeeks: value.initialWeeks,
    remainingWeeks: value.remainingWeeks,
    occurredSeason: value.occurredSeason,
    occurredRound: value.occurredRound,
    isRecurrence: value.isRecurrence,
    recurrenceCount: value.recurrenceCount,
    ...(value.previousInjuryId ? { previousInjuryId: value.previousInjuryId } : {}),
  }
}

/** Treat persisted medical data as unknown and return only canonical injuries. */
export const hydratePlayerInjuries = (player: Player): Player => {
  const persisted = player as unknown as Record<string, unknown>
  const injury = hydrateInjury(persisted.injury, player, true)
  const history = Array.isArray(persisted.injuryHistory)
    ? persisted.injuryHistory.map(entry => hydrateInjury(entry, player, false)).filter((entry): entry is PlayerInjury => Boolean(entry))
    : []
  if (injury && !history.some(entry => entry.id === injury.id)) history.push({ ...injury })
  return { ...player, injury, injuryHistory: history }
}

const cloneTeam = (team: TeamSelection): TeamSelection => Object.fromEntries(
  Object.entries(team).map(([group, entries]) => [group, (entries as TeamPlayer[]).map(entry => ({ ...entry }))]),
) as unknown as TeamSelection

const repairTeam = (team: TeamSelection, players: Player[]): TeamSelection => {
  const next = cloneTeam(team)
  const byId = new Map(players.map(player => [player.id, player]))
  const seniorIds = new Set(SENIOR_GROUPS.flatMap(group => next[group].map(entry => entry.playerId)))
  const unavailableSlots = SENIOR_GROUPS.flatMap(group => next[group]
    .map((entry, index) => ({ group, index, entry }))
    .filter(slot => Boolean(byId.get(slot.entry.playerId)?.injury)))

  for (const slot of unavailableSlots) {
    const candidates = players
      .filter(player => !player.injury && !seniorIds.has(player.id))
      .sort((a, b) => {
        const aFit = a.position === slot.entry.position ? 0 : a.position === 'INT' || a.position === 'EMG' ? 1 : 2
        const bFit = b.position === slot.entry.position ? 0 : b.position === 'INT' || b.position === 'EMG' ? 1 : 2
        return aFit - bFit || b.fitness - a.fitness || a.id.localeCompare(b.id)
      })
    const replacement = candidates[0]
    if (!replacement) continue
    const emergencyIndex = next.emergencies.findIndex(entry => entry.playerId === replacement.id)
    if (emergencyIndex >= 0) next.emergencies[emergencyIndex] = { ...slot.entry, position: 'EMG' }
    else {
      const injuredEmergency = next.emergencies.findIndex(entry => Boolean(byId.get(entry.playerId)?.injury))
      if (injuredEmergency >= 0) next.emergencies[injuredEmergency] = { ...slot.entry, position: 'EMG' }
    }
    next[slot.group][slot.index] = { playerId: replacement.id, position: slot.entry.position, role: replacement.primaryRole }
    seniorIds.delete(slot.entry.playerId)
    seniorIds.add(replacement.id)
  }
  return next
}

/** Explain why a submitted senior side cannot be resolved. */
export const teamSelectionIssue = (team: TeamSelection, players: Player[]): string | null => {
  const entries = SENIOR_GROUPS.flatMap(group => Array.isArray(team[group]) ? team[group] : [])
  if (entries.length !== 22) return `The senior team is incomplete (${entries.length}/22 selected).`
  const ids = entries.map(entry => entry?.playerId)
  if (ids.some(id => typeof id !== 'string')) return 'The senior team contains an invalid selection.'
  if (new Set(ids).size !== ids.length) return 'The senior team contains a duplicate player.'
  const byId = new Map(players.map(player => [player.id, player]))
  if (ids.some(id => !byId.has(id))) return 'The senior team contains a player who is no longer on the list.'
  const injured = ids.map(id => byId.get(id)).find(player => player?.injury)
  if (injured?.injury) return `${injured.name} is unavailable with a ${injured.injury.severity} ${injured.injury.type}; there is not enough healthy depth to field 22 players.`
  return null
}

/** Repair the next week's editable and canonical staff teams, never an already-resolved report. */
export const repairUnavailableSelections = (state: GameState): GameState => {
  const team = repairTeam(state.staffPlan.team, state.club.players)
  const staffDefaults = state.week?.staffDefaults
    ? { ...state.week.staffDefaults, team: repairTeam(state.week.staffDefaults.team, state.club.players) }
    : undefined
  const seniorIds = new Set(SENIOR_GROUPS.flatMap(group => team[group].map(entry => entry.playerId)))
  return {
    ...state,
    club: {
      ...state.club,
      players: state.club.players.map(player => ({
        ...player,
        isSelected: seniorIds.has(player.id),
        positionInTeam: seniorIds.has(player.id)
          ? SENIOR_GROUPS.flatMap(group => team[group]).find(entry => entry.playerId === player.id)?.position ?? null
          : null,
        roleInTeam: seniorIds.has(player.id)
          ? SENIOR_GROUPS.flatMap(group => team[group]).find(entry => entry.playerId === player.id)?.role ?? null
          : null,
      })),
    },
    staffPlan: { ...state.staffPlan, team },
    week: state.week ? { ...state.week, staffDefaults } : state.week,
  }
}

export const withGeneratedInjuries = (result: GameState['matchResult'], state: GameState): NonNullable<GameState['matchResult']> => {
  if (!result) throw new Error('A match result is required')
  const injuries = generateMatchInjuries(state)
  return {
    ...result,
    injuries,
    keyEvents: [
      ...result.keyEvents,
      ...injuries.map((injury, index) => ({
        quarter: 2 + (index % 3),
        time: index % 2 ? '17:40' : '08:15',
        player: injury.player,
        type: 'injury' as const,
        description: injury.description,
      })),
    ],
  }
}
